import React, { useEffect, useState } from 'react';
import { FaRegClock } from 'react-icons/fa';
import { useSelector, useDispatch } from 'react-redux';
import chatSlice, { chatSliceAction } from '../../store/chatSlice';
import { Dispatch } from "redux";
import styled, { keyframes, css } from "styled-components";
import { emailSliceAction } from '../../store/emailSlice';
import contactAttributeSlice, { contactAttributeSliceAction } from '../../store/contactAttributeSlice';

const newMessage = keyframes`
    0% {
        background: white;
    }
    100%{
        background: #f4cccc;
    }
`;

const animationRule = css`
    ${newMessage} 0.5s infinite alternate;
`;

const Contact = styled("button")<{ isSelected: boolean; hasNotification: boolean }>`
    animation: ${(props) => (props.hasNotification ? animationRule : ";")}
    background: ${(props) => (props.isSelected ? "#E7FFCA" : "white")};
    border-right: ${(props) => (props.hasNotification ? "0.3em solid #4A6FDE;" : ";")}
`;

// Definir una interfaz para los props del UserComponent
interface UserComponentProps {
    chat: any;
    dispatch: Dispatch<any>;
    lastCustomerMessage?: any;
    onCustomerMessageReceived?: (chatId: string, lastCustomerMessage: any) => void;
}

const UserComponent: React.FC<UserComponentProps> = ({
    chat, 
    dispatch, 
    lastCustomerMessage, 
    onCustomerMessageReceived
}) => {
    const chatSliceData = useSelector((state: any) => state.chatSlice);
    const [time, setTime] = useState(Math.round(Date.now() / 1000) - chat["absoluteTime"]);
    const [ownMessageTimer, setOwnMessageTimer] = useState(0);
    const [timer, setTimer] = useState("");
    const [ownTimerString, setOwnTimerString] = useState("");
    const selectedChatId = chatSliceData.selectedChat;
    const isSelected = selectedChatId === chat["id"];

    const getACWTimeout = (contactInfo: any) => {
        let timeout = 0;
        Object.keys(contactInfo).map((key) => {
            if (key === "ACWTimeout") {
                timeout = parseInt(contactInfo[key]["value"]);
            }
        });
        return timeout;
    };

    useEffect(() => {
        let interval = setInterval(() => {
            setTime(Math.round(Date.now() / 1000) - chat["absoluteTime"]);
            setOwnMessageTimer((prev) => prev + 1);

            // Encontrar el último mensaje que no sea del agente (customer)
            const lastCustomerMessageInChat = chat["conversation"].slice().reverse().find(
                (msg: any) => !msg["isYours"] && !msg["isSystemMessage"]
            );

            if (lastCustomerMessageInChat) {
                // Si el último mensaje del customer es diferente al último mensaje de customer rastreado
                if (lastCustomerMessage !== lastCustomerMessageInChat) {
                    // Resetear el timer del customer
                    setOwnMessageTimer(0);
                    onCustomerMessageReceived?.(chat["id"], lastCustomerMessageInChat);
                }
            }

            if (
                chat["isEnded"] &&
                time === getACWTimeout(chat["contactInfo"]) &&
                getACWTimeout(chat["contactInfo"])
            ) {
                dispatch(chatSliceAction.closeContact({ chatId: chat["id"] }));
            }

            // Formatear el temporizador general
            var seconds = time % 60;
            var minutes = Math.floor(time / 60) % 60;
            var hours = Math.floor(Math.floor(time / 60) / 60);

            let str = "";

            if (hours !== 0) {
                str = str + hours.toString().padStart(2, "0") + ":";
            }

            str += minutes.toString().padStart(2, "0") + ":";
            str += seconds.toString().padStart(2, "0");

            setTimer(str);

            // Formatear el temporizador específico del cliente
            const ownSeconds = ownMessageTimer % 60;
            const ownMinutes = Math.floor(ownMessageTimer / 60) % 60;
            const ownHours = Math.floor(ownMessageTimer / 3600);

            let ownStr = "";

            if (ownHours !== 0) {
                ownStr += ownHours.toString().padStart(2, "0") + ":";
            }
            ownStr += ownMinutes.toString().padStart(2, "0") + ":";
            ownStr += ownSeconds.toString().padStart(2, "0");

            setOwnTimerString(ownStr);

        }, 1000);

        return () => clearInterval(interval);
    }, [time, chat, lastCustomerMessage, onCustomerMessageReceived]);

    function getInitials(name: string) {
        let initials = "";
        if (name.length > 0) {
            let firstLetters = name.split(/\s/).reduce((response, word) => response += word.slice(0, 1), '');
            initials = firstLetters.substring(0, 2);
        }
        return initials;
    }

    return (
        <li key={chat["id"]}>
            <div style={{ display: "flex", flexDirection: "column" }}>
                <Contact
                    hasNotification={chat["hasNotification"]}
                    isSelected={isSelected}
                    className='chat-user-box'
                    onClick={() => {
                        dispatch(chatSliceAction.changeSelectedChat({ newSelectedChat: chat["id"] }));
                        dispatch(chatSliceAction.removeNotification({ participantId: chat["id"] }));
                        if ("DefaultFlowForAgentUI" in chatSliceData.selectedInfo) {
                            const contactflowId = chatSliceData.selectedInfo["DefaultFlowForAgentUI"]["value"];
                            const customViewsIframe = document.querySelector('#customviews-container')!;
                            const currentContactId = chatSliceData.selectedChat;
                            customViewsIframe.setAttribute('src', `${process.env.REACT_APP_AWS_CCP_SBS_URL}stargate/app?contactFlowId=${contactflowId}&currentContactId=${currentContactId}`);
                        }
                    }}>
                    <div className='chat-user-icon'>
                        {getInitials(chat["name"])}
                    </div>
                    <div className='chat-user-name'>
                        <div className='chuser-name'>{chat["name"]}</div>
                        <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.2rem' }}>
                            <FaRegClock style={{ marginBottom: '0.1rem', marginRight: '0.3rem' }} />
                            <span style={{ fontSize: '14px' }}>Total: {timer}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.2rem' }}>
                            <FaRegClock style={{ marginBottom: '0.1rem', marginRight: '0.3rem' }} />
                            <span style={{ fontSize: '14px' }}>Customer: {ownTimerString}</span>
                        </div>
                        {chat["conversation"].length > 0 ? (
                            <div className='chuser-msg'>
                                {(chat["conversation"].at(-1)["isYours"] ? ("You: ") : ((chat["conversation"].at(-1)["isSystemMessage"]) ? ("") : (chat["name"] + ": "))) +
                                    (chat["conversation"].at(-1)["message"].length > 15 ?
                                        (chat["conversation"].at(-1)["message"].slice(0, 15) + "...") :
                                        (chat["conversation"].at(-1)["message"]))}
                            </div>
                        ) : (
                            <div className='chuser-msg'>{" "}</div>
                        )}
                    </div>

                </Contact>
                <div className="dropdown" style={{ marginTop: "1.45rem", right: "0", position: "absolute", marginRight: "0.75rem" }}>
                    <button type="button" className="btn btn-sm border-0" data-bs-toggle="dropdown">
                        <i className="fa-solid fa-ellipsis-vertical"></i>
                    </button>
                    <ul className="dropdown-menu">
                        <button className="dropdown-item" onClick={() => {
                            dispatch(chatSliceAction.addNotification({ participantId: chat["id"] }))
                        }}>
                            Mark as Unread
                        </button>

                        <button className="dropdown-item" onClick={() => {
                            let contact = chat.contactObject;
                            let attributes = contact.getAttributes();

                            let value = !chatSliceData.isTranslationDisabledByAgent;

                            attributes.TranslationDisabledByAgent = { "name": "TranslationDisabledByAgent", "value": value ? "True" : "False" };

                            let contactData: any = {};
                            contactData.contactAttributes = attributes;
                            dispatch(contactAttributeSliceAction.addContactAttributes(contactData));
                            dispatch(chatSliceAction.changeIsTranslationDisabledByAgent({ isTranslationDisabledByAgent: value }));

                            console.log("Translation for this chat is now set to " + chatSliceData.isTranslationDisabledByAgent)
                        }}>
                            Toggle Translation
                        </button>
                    </ul>
                </div>
            </div>
        </li>
    );
};

const ChatUser = () => {
    const chats = useSelector((state: any) => state.chatSlice.chats);
    const lastAction = useSelector((state: any) => state.chatSlice.lastAction);
    const dispatch = useDispatch();
    const [lastCustomerMessages, setLastCustomerMessages] = useState<{[key: string]: any}>({});

    const handleCustomerMessageReceived = (chatId: string, lastCustomerMessage: any) => {
        setLastCustomerMessages(prev => ({
            ...prev,
            [chatId]: lastCustomerMessage
        }));
    };

    return (
        <div className="card wrapup-box collapse show mt-3" id="ChatUser">
            <div className="card-header d-flex justify-content-between align-items-center">
                <h6 className='m-0'>Chats</h6>
            </div>
            <div className="card-body height-400">
                <ul className='list-unstyled m-0'>
                    {Object.keys(chats[0]).length === 0 ? (
                        <></>
                    ) : (
                        chats.map((chat: { [key: string]: any }) => (
                            <UserComponent
                                chat={chat}
                                dispatch={dispatch}
                                key={chat["id"]}
                                lastCustomerMessage={lastCustomerMessages[chat["id"]]}
                                onCustomerMessageReceived={handleCustomerMessageReceived}
                            />
                        ))
                    )}
                </ul>
            </div>
        </div>
    );
};

export default ChatUser;